import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { useEffect, useState } from "react";
import { ConfigPagamentoForm, ConfigPagamentoSchema } from "../../../schemas/Medicao/config_pagamento";
import { ConfigPagamentoLista } from "../../../../domain/models/config_pagamento_lista";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { FormNFEspecialMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_nota_fiscal_especial_masked_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedConfigPag: ConfigPagamentoLista | undefined,
  editmod: boolean;
  onClose: () => void;
  onSubmit: (e: ConfigPagamentoForm) => void;
  onSubmitDeletar: (openState: boolean) => void;
};

export const ConfigPagamentoModal = ({
  open,
  saving,
  loading,
  selectedConfigPag,
  onClose,
  onSubmit,
  onSubmitDeletar,
}: Props) => {
  const hookForm = useForm<ConfigPagamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ConfigPagamentoSchema),
  });
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const aVista = hookForm.watch('AVista');
  const dias = hookForm.watch('TipoData');

  useEffect(() => {
    if (open) {
      hookForm.reset();
      if (selectedConfigPag) {
        if (selectedConfigPag.DataPgto !== null) setReadOnly(true);
        else setReadOnly(false);


        const aVistaNumber = selectedConfigPag.AVista === true ? 1 : (selectedConfigPag.AVista === false ? 0 : 1);
        const TipoData = selectedConfigPag.TipoData === 1 ? 1 : (selectedConfigPag.TipoData === 0 ? 0 : 0);
        hookForm.setValue('AVista', aVistaNumber);
        hookForm.setValue('DataEntrada', selectedConfigPag.DataEntrada);
        hookForm.setValue('DataPgto', selectedConfigPag.DataPgto);
        hookForm.setValue('FormaPagamento', selectedConfigPag.FormaPagamento);
        hookForm.setValue('IdMedicaoTerceirizacao', selectedConfigPag.IdMedicaoTerceirizacao);
        hookForm.setValue('IdPgtoMedicao', selectedConfigPag.IdPgtoMedicao);
        hookForm.setValue('IdSubconta', selectedConfigPag.IdSubconta);
        hookForm.setValue('IdTipoConta', selectedConfigPag.IdTipoConta);
        hookForm.setValue('InformacoesGerais', selectedConfigPag.InformacoesGerais);
        hookForm.setValue('Nome', selectedConfigPag.Nome);
        hookForm.setValue('NrNotaFiscalEspecial', selectedConfigPag.NrNotaFiscalEspecial);
        hookForm.setValue('NrParcelas', selectedConfigPag.NrParcelas);
        hookForm.setValue('Observacao', selectedConfigPag.Observacao);
        hookForm.setValue('TipoData', TipoData);
        hookForm.setValue('ValorEntrada', selectedConfigPag.ValorEntrada);
        hookForm.setValue('ValorTotal', selectedConfigPag.ValorTotal);
        hookForm.setValue('NomeFornecedor', selectedConfigPag.NomeFornecedor);
      }
    }
  }, [hookForm, open, selectedConfigPag]);

  return (
    <BaseFormControlledModal
      width="1200px"
      open={open}
      saving={saving}
      loading={loading}
      title={'Configurar Pagamento'}
      onClose={onClose}
      onSubmit={!readOnly ? hookForm.handleSubmit(onSubmit) : () => { }}>
      <FormContainer title={""} arrangement="fullRow">
        <div className="flex flex-col gap-4">
          {selectedConfigPag?.IdPgtoMedicao ? (
            <div className="flex justify-end mb-2 w-full">
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="Delete" size={20} className="text-white" />}
                onClick={() => onSubmitDeletar(true)}
                buttonColor={"red"}
                size="small"
                style={{ width: 'auto', minWidth: 'unset', padding: '6px 16px' }}
              >
                Excluir Configuração
              </PrimaryButton>
            </div>
          ) : null}
          <DefaultFormInput
            name='NomeFornecedor'
            label='Nome Fornecedor'
            readOnly={true}
            reactHookForm={hookForm} />
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='ValorTotal'
              label='Valor Total'
              required={true}
              readOnly={true}
              reactHookForm={hookForm} />
            <DefaultFormInput
              name='Nome'
              label='Título'
              readOnly={readOnly}
              reactHookForm={hookForm} />
          </div>
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='FormaPagamento'
              label='Forma de Pagamento'
              readOnly={readOnly}
              reactHookForm={hookForm} />
            <FormNFEspecialMaskedInput
              name="NrNotaFiscalEspecial"
              label="Nota Fiscal Especial"
              readOnly={readOnly}
              reactHookForm={hookForm}
              required={false}
            />
          </div>
          <div className="flex flex-row gap-x-8">
            <FormRadioGroupInput
              name='AVista'
              label='Pagamento à Vista'
              options={[
                { id: 0, label: 'Não' },
                { id: 1, label: 'Sim' },
              ]}
              reactHookForm={hookForm} />
          </div>

          {/* CONDITIONAL BLOCK: Pagamento Parcelado (Não À Vista) */}
          {(Number(aVista) === 0 && readOnly === false) && (
            <>
              <div className="flex flex-row gap-x-8">
                <FormRadioGroupInput
                  name='TipoData'
                  label='Pagamento em'
                  readOnly={readOnly}
                  options={[
                    { id: 0, label: 'Projetar meses futuros' },
                    { id: 1, label: 'Informar intervalo de dias' },
                  ]}
                  reactHookForm={hookForm} />
              </div>

              {/* Conditional Row for 'Dias' Input */}
              {(Number(dias) === 1) && (
                <div className="flex flex-row gap-x-8">
                  <DefaultFormInput
                    name='DiasEntreParcelas'
                    label='Número de dias entre as parcelas?'
                    readOnly={readOnly}
                    reactHookForm={hookForm} />
                </div>
              )}
              <div className="flex flex-row gap-x-8">
                <FormMoneyMaskedInput
                  name={"ValorEntrada"}
                  label={"Valor de Entrada"}
                  largeNumbers={readOnly}
                  readOnly={readOnly}
                  reactHookForm={hookForm} />
                <FormDatePickerInput
                  name='DataEntrada'
                  label='Data de Entrada'
                  reactHookForm={hookForm} />
              </div>
              <div className="flex flex-row gap-x-8">
                <DefaultFormInput
                  name='NrParcelas'
                  label='Gerar Quantas Parcelas?'
                  readOnly={readOnly}
                  reactHookForm={hookForm} />
                <FormDatePickerInput
                  name='DataPgto'
                  readOnly={readOnly}
                  label='Data do 1º Pagamento'
                  reactHookForm={hookForm} />
              </div>
            </>
          )}

          {/* CONDITIONAL BLOCK: Pagamento À Vista (Sim) */}
          {(Number(aVista) !== 0) && (
            <div className="flex flex-row gap-x-8">
              <FormDatePickerInput
                name='DataPgto'
                label='Data Pagamento'
                readOnly={readOnly}
                reactHookForm={hookForm}
              />
            </div>
          )}
          <div className="flex flex-row gap-x-8">
            <DefaultFormInput
              name='InformacoesGerais'
              label='Informacoes Gerais'
              readOnly={readOnly}
              reactHookForm={hookForm} />
          </div>

        </div>
      </FormContainer>
    </BaseFormControlledModal>
  );
}